import React, {ReactNode} from 'react'
import {Button as MuiButton, ButtonProps as MuiButtonProps, CircularProgress, useTheme} from '@mui/material'
import {CONTROL_HEIGHT, NEUTRAL_SCALE} from 'src/theme-new/tokens'

export interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'color'> {
  variant?: 'primary' | 'secondary' | 'dark' | 'ghost' | 'danger'
  startIcon?: ReactNode
  endIcon?: ReactNode
  loading?: boolean
}

export default function Button({
  variant = 'primary',
  startIcon,
  endIcon,
  loading = false,
  disabled,
  children,
  sx,
  ...rest
}: ButtonProps): JSX.Element {
  const theme = useTheme()

  const variantStyles: Record<NonNullable<ButtonProps['variant']>, object> = {
    primary: {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
      '&:hover': {backgroundColor: theme.palette.primary.dark},
      '&:disabled': {opacity: 0.5},
      boxShadow: 'none',
    },
    secondary: {
      backgroundColor: 'transparent',
      color: NEUTRAL_SCALE[900],
      border: `1px solid ${NEUTRAL_SCALE[300]}`,
      '&:hover': {backgroundColor: NEUTRAL_SCALE[50], borderColor: NEUTRAL_SCALE[500]},
      '&:disabled': {opacity: 0.5},
    },
    dark: {
      backgroundColor: NEUTRAL_SCALE[900],
      color: '#fff',
      boxShadow: '0px 2px 4px rgba(44,62,80,0.15)',
      '&:hover': {backgroundColor: NEUTRAL_SCALE[700]},
      '&:disabled': {opacity: 0.5},
    },
    ghost: {
      backgroundColor: 'transparent',
      color: theme.palette.info.main,
      '&:hover': {backgroundColor: theme.palette.info.light},
      '&:disabled': {opacity: 0.5},
    },
    danger: {
      backgroundColor: theme.palette.error.main,
      color: '#fff',
      '&:hover': {backgroundColor: theme.palette.error.dark},
      '&:disabled': {opacity: 0.5},
      boxShadow: 'none',
    },
  }

  const muiVariant = variant === 'secondary' ? 'outlined' : variant === 'ghost' ? 'text' : 'contained'

  return (
    <MuiButton
      variant={muiVariant}
      size="small"
      disabled={disabled || loading}
      startIcon={loading ? <CircularProgress size={14} color="inherit" /> : startIcon}
      endIcon={endIcon}
      sx={{
        height: CONTROL_HEIGHT,
        borderRadius: '12px',
        textTransform: 'none',
        fontWeight: 500,
        fontSize: 14,
        px: 2,
        ...variantStyles[variant],
        ...sx,
      }}
      {...rest}
    >
      {children}
    </MuiButton>
  )
}
